import { useState } from 'react'

import { SelectField, type SelectFieldProps } from '@components/molecules/SelectField'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<SelectFieldProps<string>> = {
  name: { type: 'string', required: true, description: 'Field name. Builds the error and hint ids.' },
  label: { type: 'string', required: true, description: 'Visible label. It names the select for screen readers.' },
  options: {
    type: 'SelectFieldOption<T>[]',
    required: true,
    description: 'Each option is { value, label, disabled? }. Values are strings.',
  },
  value: {
    type: 'T | null | undefined',
    required: true,
    description: 'The chosen value. Empty shows the placeholder.',
  },
  onValueChange: {
    type: '(value: T) => void',
    required: true,
    description: 'Called with the chosen value. With emptyOptionLabel it can be called with null.',
  },
  emptyOptionLabel: {
    type: 'string',
    description: 'Adds a first item, like “All groups”, that sets the value back to null.',
  },
  placeholder: { type: 'string', default: "'Select...'", description: 'Shown while nothing is chosen.' },
  description: { type: 'string', description: 'Hint under the select.' },
  errors: { type: 'FieldErrors', description: 'react-hook-form errors. The error for name shows under the select.' },
  required: { type: 'boolean', default: 'false', description: 'Adds “(required)” to the label.' },
  disabled: { type: 'boolean', default: 'false', description: 'Stops the menu opening.' },
  hideLabel: { type: 'boolean', default: 'false', description: 'Keeps the label for screen readers only.' },
  search: {
    type: 'boolean',
    default: 'false',
    description:
      'Adds a search box to the menu that filters the options. Options starting with the query rank first, Enter picks the first match, and screen readers hear the match count.',
  },
  className: { type: 'string', description: 'Merged with cn on the wrapper.' },
  ref: {
    type: 'Ref<HTMLButtonElement>',
    description: 'Goes on the trigger. Pass field.ref from react-hook-form so a failed submit focuses the select.',
  },
}

const REGIONS = [
  { value: 'brisbane-north', label: 'Brisbane North' },
  { value: 'brisbane-south', label: 'Brisbane South' },
  { value: 'gold-coast', label: 'Gold Coast' },
  { value: 'sunshine-coast', label: 'Sunshine Coast' },
  { value: 'toowoomba', label: 'Toowoomba' },
  { value: 'cairns', label: 'Cairns' },
]

const COUNTRIES = [
  'Argentina',
  'Australia',
  'Austria',
  'Canada',
  'Fiji',
  'France',
  'Germany',
  'Ireland',
  'Italy',
  'New Zealand',
  'Papua New Guinea',
  'United Kingdom',
].map((country) => ({ value: country.toLowerCase().replaceAll(' ', '-'), label: country }))

function Region({ error, disabled }: { error?: string; disabled?: boolean }) {
  const [value, setValue] = useState<string | null>(null)
  return (
    <div className="tw:w-full tw:max-w-md">
      <SelectField
        name="region"
        label="Region"
        description="Where the supporter usually volunteers."
        placeholder="Choose a region"
        options={REGIONS}
        value={value}
        onValueChange={setValue}
        errors={error ? { region: { type: 'required', message: error } } : undefined}
        required
        disabled={disabled}
      />
    </div>
  )
}

function GroupFilter() {
  const [value, setValue] = useState<string | null>(null)
  return (
    <div className="tw:w-full tw:max-w-xs">
      <SelectField
        hideLabel
        name="group-filter"
        label="Group"
        emptyOptionLabel="All groups"
        options={REGIONS}
        value={value}
        onValueChange={setValue}
      />
    </div>
  )
}

function Country() {
  const [value, setValue] = useState<string | null>('australia')
  return (
    <div className="tw:w-full tw:max-w-md">
      <SelectField search name="country" label="Country" options={COUNTRIES} value={value} onValueChange={setValue} />
    </div>
  )
}

const doc: ComponentDoc<SelectFieldProps<string>> = {
  slug: 'select-field',
  name: 'SelectField',
  tier: 'molecule',
  description:
    'The select for every form and toolbar. One choice from a list that is too long for radios, with a label, hint and error. Turn on search for long lists.',
  importStatement: "import { SelectField } from '@components/molecules/SelectField'",
  sourcePath: 'app/frontend/components/molecules/SelectField.tsx',
  props,
  examples: [
    {
      title: 'In a form',
      note: 'Pass the field from useController. The label, hint and error are linked to the select for you.',
      render: () => <Region />,
      code: `const { field } = useController({ name: 'region', control })

<SelectField
  name={field.name}
  label="Region"
  description="Where the supporter usually volunteers."
  placeholder="Choose a region"
  options={regions}
  value={field.value}
  onValueChange={field.onChange}
  errors={errors}
  required
/>`,
    },
    {
      title: 'With an error',
      render: () => <Region error="Choose a region" />,
      code: `<SelectField name="region" errors={errors} ... />`,
    },
    {
      title: 'An option that clears it',
      note: 'For an optional filter or field. The first item sets the value back to null, and shows as the value while nothing is chosen.',
      render: () => <GroupFilter />,
      code: `<SelectField
  hideLabel
  name="group-filter"
  label="Group"
  emptyOptionLabel="All groups"
  options={groups}
  value={groupId}
  onValueChange={setGroupId}
/>`,
    },
    {
      title: 'Search',
      note: 'For long lists like countries. The menu gets a search box that filters the options.',
      render: () => <Country />,
      code: `<SelectField search name="country" label="Country" options={countries} value={value} onValueChange={setValue} />`,
    },
    {
      title: 'Disabled',
      note: 'Only while a request is in flight, or when the page says why it is unavailable.',
      render: () => <Region disabled />,
      code: `<SelectField disabled ... />`,
    },
  ],
  usage: {
    do: [
      'Use it for every select, in forms and toolbars alike.',
      'Use it for five or more options. For fewer, use a RadioGroupField.',
      'Turn on search when people would otherwise scroll a long list.',
      'Use hideLabel in a toolbar where the options explain themselves. Keep the label visible in a form.',
    ],
    dont: [
      'Use it to pick several things. Use MultiSelectField, which has the same label, hint and error.',
      'Use it when the options come from a search. Use AsyncSelect.',
      'Add a “None” option by hand. Use emptyOptionLabel.',
    ],
  },
  a11y: [
    'The label names the select. The hint and error are linked with aria-describedby.',
    'An error sets aria-invalid, and the border turns red.',
    'The trigger is a button with role combobox and aria-expanded. The list is a listbox and each item an option.',
    'Space, Enter and the arrow keys open the list, arrows move and Escape closes it.',
    'Without search, typing jumps to the first matching option. With search, typing filters the list and screen readers hear the match count.',
    'The chosen option carries a check mark.',
    'A disabled option stays in the list for context but is skipped by the keyboard.',
    'The menu opens above a Dialog and is never clipped by a scrolling panel.',
    'The open and close animations are skipped when the system reduce motion setting is on.',
  ],
}

export default doc
